import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  InputAdornment,
  Autocomplete,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  List as ListIcon,
  Save as SaveIcon,
  Reply as ReplyIcon,
  CheckCircle as DoneIcon,
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { getPaymentAssociates, payIncentive, getPaymentEntries, createAssociateLedger, saveIncentivePayment } from '../../services/associate';
import AlertModal from '../../components/AlertModal';

const PayIncentive = () => {
  const theme = useTheme();
  const history = useHistory();
  const [formData, setFormData] = useState({
    // voucherNo: '', // Commented out
    associate: null,
    overallCommission: '',
    tds: false,
    tdsPercent: 10,
    tdsAmount: '',
    selectBank: '',
    chequeNo: '',
    booking: null,
    remark: '',
    voucherDate: new Date().toISOString().split('T')[0],
    paidAmount: '',
    balance: '',
    amount: '',
    charge: false,
    chargeAmount: '',
    paymentMode: '',
    accountNo: '',
    chequeDate: '',
    bookingNo: '',
    totalPendingAmount: '',
  });

  // Associates state
  const [associates, setAssociates] = useState([]);
  const [loadingAssociates, setLoadingAssociates] = useState(false);
  const [associatePage, setAssociatePage] = useState(1);
  const [hasMoreAssociates, setHasMoreAssociates] = useState(true);
  const [associateTotalPages, setAssociateTotalPages] = useState(1);

  // Bookings state
  const [bookingOptions, setBookingOptions] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);

  // Commission data state
  const [loadingCommission, setLoadingCommission] = useState(false);

  // Save state
  const [saving, setSaving] = useState(false);

  // Modal state
  const [alertConfig, setAlertConfig] = useState({
    open: false,
    title: 'Alert',
    message: '',
    type: 'info',
  });

  const showAlert = (message, type = 'info', title = 'Alert') => {
    setAlertConfig({ open: true, title, message, type });
  };

  // Mock banks data
  const banks = [
    'ALLAHABAD BANK',
    'STATE BANK OF INDIA',
    'HDFC BANK',
    'ICICI BANK',
    'AXIS BANK',
    'PUNJAB NATIONAL BANK',
  ];

  // Mock projects data
  const projects = [
    'Green Valley',
    'Sunset Heights',
    'Royal Gardens',
    'Prestige Park',
  ];

  // Mock payment heads
  const paymentHeads = [
    'Incentive',
    'Commission Paid',
    'Advance Payment',
    'Bonus',
  ];

  // Mock payment modes
  const paymentModes = [
    'Cheque',
    'Cash',
    'Bank Transfer',
    'UPI',
    'NEFT',
    'RTGS',
  ];

  // Load associates
  const loadAssociates = useCallback(async (page = 1, append = false, searchTerm = '') => {
    setLoadingAssociates(true);
    try {
      const response = await getPaymentAssociates({ page, limit: 100, search: searchTerm });
      if (response.success && response.data) {
        const formattedAssociates = response.data.map((associate) => ({
          id: associate.associateId,
          name: associate.associateName || '',
          code: associate.associateCode || '',
          phone: associate.phone || '',
          email: associate.email || '',
          designation: associate.designation || '',
          totalPendingAmount: associate.totalPendingAmount || 0,
          pendingEntries: associate.pendingEntries || 0,
          displayLabel: associate.displayLabel || `${associate.associateName || ''} (${associate.associateCode || ''}) - Pending Rs. ${associate.totalPendingAmount || 0}`,
          rawData: associate
        }));

        if (append) {
          setAssociates((prev) => [...prev, ...formattedAssociates]);
        } else {
          setAssociates(formattedAssociates);
          setAssociatePage(1);
        }

        if (response.pagination) {
          setAssociateTotalPages(response.pagination.totalPages || 1);
          setHasMoreAssociates(page < (response.pagination.totalPages || 1));
        } else {
          setHasMoreAssociates(formattedAssociates.length === 100);
        }
      }
    } catch (error) {
      console.error('Error fetching associates:', error);
      if (!append) {
        setAssociates([]);
      }
    } finally {
      setLoadingAssociates(false);
    }
  }, []);

  // Load initial associates
  useEffect(() => {
    loadAssociates(1, false);
  }, [loadAssociates]);

  // Handle associate search with debounce
  const [associateSearchInput, setAssociateSearchInput] = useState('');
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      loadAssociates(1, false, associateSearchInput);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [associateSearchInput, loadAssociates]);

  // Load more associates when scrolling
  const loadMoreAssociates = useCallback(async () => {
    if (loadingAssociates || !hasMoreAssociates) return;

    const nextPage = associatePage + 1;
    setAssociatePage(nextPage);

    await loadAssociates(nextPage, true, associateSearchInput);
  }, [associatePage, hasMoreAssociates, loadingAssociates, loadAssociates, associateSearchInput]);

  // Handle scroll in associate dropdown - trigger at 80-90% scroll
  const handleAssociateListboxScroll = useCallback((event) => {
    const listboxNode = event.currentTarget;
    const scrollTop = listboxNode.scrollTop;
    const scrollHeight = listboxNode.scrollHeight;
    const clientHeight = listboxNode.clientHeight;

    // Calculate scroll percentage
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    // Load more when user scrolls to 80-90% of the content
    if (scrollPercentage >= 0.8 && scrollPercentage <= 0.9 && hasMoreAssociates && !loadingAssociates) {
      loadMoreAssociates();
    }
  }, [hasMoreAssociates, loadingAssociates, loadMoreAssociates]);

  // Fetch bookings for selected associate (Mocked)
  const fetchBookingsList = useCallback(async (associateId) => {
    setLoadingBookings(true);
    setTimeout(() => {
      const mockBookings = [
        {
          id: 'B001',
          bookingId: 'B001',
          bookingNo: 'BK-1001',
          projectName: 'Green Valley',
          inventoryType: 'Plot',
          inventoryDetail: 'P-45',
          displayLabel: 'Booking No: BK-1001 | Green Valley | Plot | P-45',
          projectId: 'P1',
          payableCommission: '5000',
        }
      ];
      setBookingOptions(mockBookings);
      setLoadingBookings(false);
    }, 500);
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const updatedData = {
        ...prev,
        [field]: value,
      };

      // Synchronize paidAmount and amount - they should be the same
      if (field === 'paidAmount') {
        updatedData.amount = value; // Sync amount with paidAmount
      }
      if (field === 'amount') {
        updatedData.paidAmount = value; // Sync paidAmount with amount
      }

      // Auto-calculate balance when payableCommission or paid amount changes
      if (field === 'payableCommission' || field === 'paidAmount' || field === 'amount') {
        const payableCommission = parseFloat(prev.payableCommission) || 0;
        const paid = field === 'paidAmount' || field === 'amount' ? parseFloat(value) || 0 : parseFloat(prev.paidAmount) || 0;

        // Removed validation: paidAmount/amount can now be greater than payableCommission for incentives
        updatedData.balance = (payableCommission - paid).toFixed(2);
      }

      // Auto-calculate TDS amount when TDS is enabled or TDS percent/amount/paidAmount changes
      if (field === 'tds' || field === 'tdsPercent' || field === 'amount' || field === 'paidAmount') {
        if (updatedData.tds) {
          const amount = field === 'amount' || field === 'paidAmount' ? parseFloat(value) || 0 : parseFloat(updatedData.amount) || 0;
          const tdsPercent = field === 'tdsPercent' ? parseFloat(value) || 10 : parseFloat(updatedData.tdsPercent) || 10;
          const calculatedTds = (amount * tdsPercent) / 100;
          const charge = updatedData.charge ? (parseFloat(updatedData.chargeAmount) || 0) : 0;

          // Validate: TDS + Charge cannot exceed amount
          if (calculatedTds + charge > amount) {
            showAlert(`TDS (₹${calculatedTds.toFixed(2)}) + Charge (₹${charge}) = ₹${(calculatedTds + charge).toFixed(2)} cannot exceed Amount (₹${amount}). Please reduce TDS percentage or charge amount.`, 'warning', 'Invalid Calculation');
            return prev; // Return previous state without changes
          }

          updatedData.tdsAmount = calculatedTds.toFixed(2);
        } else {
          updatedData.tdsAmount = '';
        }
      }

      // Auto-calculate balance when paidAmount or amount changes
      if (field === 'paidAmount' || field === 'amount') {
        const totalPending = parseFloat(updatedData.totalPendingAmount || 0);
        const paid = parseFloat(value || 0);
        updatedData.balance = (totalPending - paid).toFixed(2);
      }

      return updatedData;
    });
  };

  // Handle associate change
  const handleAssociateChange = useCallback(async (event, newValue) => {
    // Update associate and basic info
    setFormData((prev) => ({
      ...prev,
      associate: newValue,
      booking: null, // Clear booking when associate changes
    }));

    // If associate is selected, fetch detailed payment entries
    if (newValue && newValue.id) {
      try {
        const response = await getPaymentEntries({ associateId: newValue.id });
        if (response.success) {
          const totalPending = response.totalPendingIncentiveAmount || 0;
          setFormData((prev) => ({
            ...prev,
            totalPendingAmount: String(totalPending ?? 0),
            balance: String(totalPending ?? 0),
            incentiveGeneratorId: response.data && response.data.length > 0 ? response.data[0].id || response.data[0].incentiveGeneratorId : null,
          }));
        }
      } catch (error) {
        console.error('Error fetching payment entries:', error);
      }
    }

    // Clear booking options
    setBookingOptions([]);
  }, []);

  // Handler for Booking selection (Mocked)
  const handleBookingChange = useCallback(async (event, value) => {
    setFormData((prev) => ({
      ...prev,
      booking: value,
      payableCommission: value?.payableCommission || '',
      balance: value?.payableCommission || ''
    }));
  }, []);

  const handleSave = async () => {
    // Validate required fields
    if (!formData.associate || !formData.associate.id) {
      showAlert('Please select an associate', 'warning', 'Required');
      return;
    }



    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      showAlert('Please enter a valid amount', 'warning', 'Required');
      return;
    }

    if (!formData.paymentMode) {
      showAlert('Please select a payment mode', 'warning', 'Required');
      return;
    }

    if (!formData.voucherDate) {
      showAlert('Please select a voucher date', 'warning', 'Required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        associateId: formData.associate?.id || '',
        amount: parseFloat(formData.paidAmount) || 0,
        voucherDate: formData.voucherDate || '',
        paymentMode: formData.paymentMode || '',
        remark: formData.remark || '',
        bankName: formData.selectBank || '',
        chequeNumber: formData.chequeNo || '',
        accountNumber: formData.accountNo || '',
        chequeDate: formData.chequeDate || null,
        charge: formData.charge ? (parseFloat(formData.chargeAmount) || 0) : 0,
        tdsPercent: formData.tds ? (parseFloat(formData.tdsPercent) || 0) : 0,
        csrPercent: 0,
        marketingPercent: 0,
        otherPercent: 0,
        incentiveGeneratorId: formData.incentiveGeneratorId || null,
      };

      const response = await saveIncentivePayment(payload);
      console.log('Incentive payment processed:', response);
      
      showAlert('Payment voucher saved successfully!', 'success', 'Success');

      // Reset form fields
      setFormData({
        associate: null,
        overallCommission: '',
        tds: false,
        tdsPercent: 10,
        tdsAmount: '',
        remark: '',
        voucherDate: new Date().toISOString().split('T')[0],
        paidAmount: '',
        balance: '',
        amount: '',
        charge: false,
        chargeAmount: '',
        paymentMode: '',
        accountNo: '',
        chequeDate: '',
        bookingNo: '',
      });
      setBookingOptions([]);
    } catch (error) {
      console.error('Failed to process incentive payment:', error);
      const errorMessage = error.message || error.response?.data?.message || 'Failed to process incentive payment';
      showAlert(`Error: ${errorMessage}`, 'error', 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleList = () => {
    history.push('/app/paymentAssociate/List');
  };

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, pb: 10, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: { xs: 2, md: 3 } }}>
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          <Grid item>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 1 }}>
              Payment Voucher
            </Typography>
            {/* <Typography variant="body2" color="text.secondary">
              Home Page / Payment Voucher
            </Typography> */}
          </Grid>
          <Grid item>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ListIcon />}
                onClick={handleList}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  borderRadius: 2,
                }}
              >
                List
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Payment Form */}
      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main', mb: 0 }}>
              Pay Incentive
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {/* Left Column */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                {/* Associate Selection */}
                <Grid item xs={12}>
                  <Autocomplete
                    options={associates}
                    getOptionLabel={(option) => option.displayLabel || `${option.name || ''} - ${option.code || ''}`}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    loading={loadingAssociates}
                    value={formData.associate}
                    onChange={handleAssociateChange}
                    ListboxProps={{
                      onScroll: handleAssociateListboxScroll,
                      style: { maxHeight: '300px' },
                    }}
                    inputValue={associateSearchInput}
                    onInputChange={(_, newInputValue) => setAssociateSearchInput(newInputValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Associate *"
                        size="small"
                        placeholder="Search Associate"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                              {loadingAssociates ? <CircularProgress color="inherit" size={18} /> : null}
                              {params.InputProps.endAdornment}
                            </React.Fragment>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {option.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                          {option.code}
                        </Typography>
                      </Box>
                    )}
                    noOptionsText={loadingAssociates ? 'Loading associates...' : 'No associates found'}
                  />
                </Grid>





                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.tds}
                        onChange={(e) => handleInputChange('tds', e.target.checked)}
                      />
                    }
                    label="TDS"
                  />
                </Grid>

                {formData.tds && (
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="TDS %"
                          type="number"
                          value={formData.tdsPercent}
                          onChange={(e) => handleInputChange('tdsPercent', e.target.value)}
                          size="small"
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="TDS Amount"
                          value={formData.tdsAmount}
                          onChange={(e) => handleInputChange('tdsAmount', e.target.value)}
                          size="small"
                          InputProps={{
                            readOnly: true,
                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Remark"
                    multiline
                    rows={3}
                    value={formData.remark}
                    onChange={(e) => handleInputChange('remark', e.target.value)}
                    placeholder="Enter Remark"
                    size="small"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                    fullWidth
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      py: 1.5,
                      borderRadius: 2,
                    }}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            {/* Right Column */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Voucher Date"
                    type="date"
                    required
                    value={formData.voucherDate}
                    onChange={(e) => handleInputChange('voucherDate', e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Paid Amount"
                    value={formData.paidAmount}
                    onChange={(e) => handleInputChange('paidAmount', e.target.value)}
                    size="small"
                    type="number"
                    inputProps={{ min: 0, step: 0.01 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          ₹
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Balance"
                    value={formData.balance}
                    onChange={(e) => handleInputChange('balance', e.target.value)}
                    size="small"
                    InputProps={{
                      readOnly: true,
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.charge}
                          onChange={(e) => handleInputChange('charge', e.target.checked)}
                        />
                      }
                      label="Charge"
                    />
                    <TextField
                      size="small"
                      value={formData.chargeAmount}
                      onChange={(e) => handleInputChange('chargeAmount', e.target.value)}
                      sx={{ width: 120 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Payment Mode</InputLabel>
                    <Select
                      value={formData.paymentMode}
                      label="Payment Mode"
                      onChange={(e) => handleInputChange('paymentMode', e.target.value)}
                    >
                      {paymentModes.map((mode) => (
                        <MenuItem key={mode} value={mode}>
                          {mode}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {formData.paymentMode !== 'Cash' && (
                  <React.Fragment>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Account No"
                        value={formData.accountNo}
                        onChange={(e) => handleInputChange('accountNo', e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Cheque Date"
                        type="date"
                        value={formData.chequeDate}
                        onChange={(e) => handleInputChange('chequeDate', e.target.value)}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </React.Fragment>
                )}
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>



      <AlertModal
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        type={alertConfig.type}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />
    </Box>
  );
};

export default PayIncentive;
